import { apiFetch } from "@/lib/api";

export type HubContactDirectorySource = "hub" | "freshsales" | "freshdesk" | "both" | "staff";

export interface HubContactSuggestion {
  email: string;
  displayName: string;
  companyName: string | null;
  jobTitle: string | null;
  source: HubContactDirectorySource;
  hubContactId: number | null;
  freshdeskContactId: number | null;
  fsContactId: number | null;
  hubUserId: string | null;
}

export function formatHubContactSuggestionSubtitle(suggestion: HubContactSuggestion): string {
  const parts = [suggestion.email];
  const company = suggestion.companyName?.trim();
  const title = suggestion.jobTitle?.trim();
  if (company) {
    parts.push(company);
  }
  if (title) {
    parts.push(title);
  }
  return parts.join(" · ");
}

export interface HubContactSelection {
  hubContactId: number;
  email: string;
  displayName: string | null;
  source: HubContactDirectorySource;
  missing: boolean;
}

export function hubContactSuggestionStub(input: {
  email: string;
  displayName?: string;
  hubContactId?: number | null;
  source?: HubContactDirectorySource;
}): HubContactSuggestion {
  return {
    email: input.email,
    displayName: input.displayName ?? input.email,
    companyName: null,
    jobTitle: null,
    source: input.source ?? "hub",
    hubContactId: input.hubContactId ?? null,
    freshdeskContactId: null,
    fsContactId: null,
    hubUserId: null,
  };
}

export async function searchHubContactSuggestions(
  query: string,
  options?: { freshdeskCompanyId?: number | null; unscoped?: boolean },
): Promise<HubContactSuggestion[]> {
  const params = new URLSearchParams({ q: query });
  if (options?.freshdeskCompanyId != null && options.freshdeskCompanyId > 0) {
    params.set("freshdesk_company_id", String(options.freshdeskCompanyId));
  }
  if (options?.unscoped) {
    params.set("unscoped", "1");
  }
  const data = await apiFetch<{ suggestions: HubContactSuggestion[] }>(
    `/api/hub/contacts/suggestions?${params.toString()}`,
  );
  return data.suggestions;
}

export async function ensureHubContactSelection(
  suggestion: HubContactSuggestion,
): Promise<HubContactSelection> {
  return apiFetch<HubContactSelection>("/api/hub/contacts/ensure", {
    method: "POST",
    body: {
      hubContactId: suggestion.hubContactId,
      email: suggestion.email,
      displayName: suggestion.displayName,
      freshdeskContactId: suggestion.freshdeskContactId,
      fsContactId: suggestion.fsContactId,
    },
  });
}

export function hubContactSelectionFromSuggestion(
  suggestion: HubContactSuggestion,
): HubContactSelection | null {
  if (suggestion.hubContactId == null) {
    return null;
  }
  return {
    hubContactId: suggestion.hubContactId,
    email: suggestion.email,
    displayName: suggestion.displayName.trim() || null,
    source: suggestion.source,
    missing: false,
  };
}

export async function resolveHubContactSelection(
  suggestion: HubContactSuggestion,
): Promise<HubContactSelection> {
  const existing = hubContactSelectionFromSuggestion(suggestion);
  if (existing != null) {
    return existing;
  }
  const ensured = await ensureHubContactSelection(suggestion);
  return {
    ...ensured,
    source: suggestion.source,
  };
}
